import { SectionConfig, SectionId, Rule, RuleColumn } from '../types';
import { ValidationResult, ValidationLevel, ValidationError } from './types';
import { validateFormulaSyntax } from '../utils/formulaValidation';

/**
 * Helper to create a ValidationResult
 */
const createValidationResult = (
  errors: ValidationError[] = [],
  warnings: ValidationError[] = []
): ValidationResult => ({
  isValid: errors.length === 0,
  errors,
  warnings,
});

/**
 * Helper to create an error
 */
const createError = (message: string): ValidationError => ({
  level: ValidationLevel.ERROR,
  message,
});

/**
 * Helper to create a warning
 */
const createWarning = (message: string): ValidationError => ({
  level: ValidationLevel.WARNING,
  message,
});

/**
 * Check if a rule column has duplicate sourceColumn values
 */
const findDuplicateColumns = (columns: RuleColumn[]): string[] => {
  const sourceColumns = columns.map(col => col.sourceColumn).filter(Boolean);
  const duplicates = sourceColumns.filter(
    (col, index) => sourceColumns.indexOf(col) !== index
  );
  return [...new Set(duplicates)];
};

/**
 * Validate a single rule
 * Used by COLUMN_MAPPING and MULTI_COLUMN_RULES_WITH_CATEGORIES
 */
const validateRule = (rule: Rule): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Error: At least one column must exist
  if (!rule.columns || rule.columns.length === 0) {
    errors.push(createError('Add at least 1 column'));
    return createValidationResult(errors, warnings);
  }

  // Error: Each column must have sourceColumn selected
  const columnsWithoutSource = rule.columns.filter(col => !col.sourceColumn);
  if (columnsWithoutSource.length > 0) {
    errors.push(createError('Select column for all entries'));
  }

  // Error: Each column must have ≥1 label
  const columnsWithoutLabels = rule.columns.filter(
    col => !col.labels || col.labels.length === 0
  );
  if (columnsWithoutLabels.length > 0) {
    errors.push(createError('Add labels to columns'));
  }

  // Warning: Duplicate columns in same rule
  const duplicates = findDuplicateColumns(rule.columns);
  if (duplicates.length > 0) {
    warnings.push(createWarning('Duplicate column'));
  }

  return createValidationResult(errors, warnings);
};

/**
 * 1. SINGLE_COLUMN
 * Examples: Symbol/ISIN, Quantity, Price, Custodian, etc.
 */
export const validateSingleColumn = (
  config: SectionConfig,
  sectionId: string
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Optional sections can be left empty
  const optionalSections = [SectionId.NOTE, SectionId.AWARDED_SHARES];
  if (optionalSections.includes(sectionId as SectionId)) {
    return createValidationResult();
  }

  // Error: Column must be selected
  const columnMappings = config.columnMappings || [];
  if (columnMappings.length === 0 || !columnMappings[0]?.sourceColumn) {
    errors.push(createError('Select column'));
  }

  return createValidationResult(errors);
};

/**
 * 2. SINGLE_COLUMN_WITH_DATE
 * Examples: Transaction Date, Settlement Date
 */
export const validateSingleColumnWithDate = (
  config: SectionConfig,
  sectionId: string
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Optional sections can be left empty
  const optionalSections = [SectionId.EX_DIVIDEND_DATE];
  if (optionalSections.includes(sectionId as SectionId)) {
    const columnMappings = config.columnMappings || [];
    const hasColumn =
      columnMappings.length > 0 && columnMappings[0]?.sourceColumn;
    const hasDateFormat = !!config.dateFormat;

    // If completely empty, it's valid (optional)
    if (!hasColumn && !hasDateFormat) {
      return createValidationResult();
    }
  }

  // Error: Column must be selected
  const columnMappings = config.columnMappings || [];
  if (columnMappings.length === 0 || !columnMappings[0]?.sourceColumn) {
    errors.push(createError('Select column'));
  }

  // Error: Date format must be selected
  if (!config.dateFormat) {
    errors.push(createError('Select date format'));
  }

  return createValidationResult(errors);
};

/**
 * 3. SINGLE_COLUMN_WITH_LABELS
 * Examples: Settlement Account
 */
export const validateSingleColumnWithLabels = (
  config: SectionConfig
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Error: Column must be selected
  const columnMappings = config.columnMappings || [];
  if (columnMappings.length === 0 || !columnMappings[0]?.sourceColumn) {
    errors.push(createError('Select column'));
  }

  // Error: At least one label is mandatory
  const labels = config.labels || [];
  if (labels.length === 0) {
    errors.push(createError('Add at least 1 label'));
  }

  return createValidationResult(errors);
};

/**
 * Check if a rule is empty/incomplete (hasn't been filled out yet)
 * A rule is considered empty if ALL its columns have:
 * - No sourceColumn selected (empty string or null)
 * - No labels added (empty array)
 */
const isRuleEmpty = (rule: Rule): boolean => {
  if (!rule.columns || rule.columns.length === 0) {
    return true;
  }

  return rule.columns.every(
    column =>
      (!column.sourceColumn || column.sourceColumn === '') &&
      (!column.labels || column.labels.length === 0)
  );
};

/**
 * 4. COLUMN_MAPPING (Instrument Identifiers)
 * Examples: Instrument Identifiers (Equities, Transfer tabs)
 */
export const validateColumnMapping = (
  config: SectionConfig
): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // COMMENTED OUT: Holdings lookup functionality disabled
  // const holdingsLookup = config.holdingsLookup;
  const rules = config.rules || [];

  // COMMENTED OUT: Holdings lookup functionality disabled
  // // Check if Holdings Lookup is properly configured
  // const hasValidLookup = holdingsLookup?.enabled && holdingsLookup.symbolColumn;

  // COMMENTED OUT: Holdings lookup functionality disabled
  // // NEW LOGIC: Holdings Lookup makes rules optional
  // // Error: If Holdings Lookup is NOT valid, user must have at least one rule
  // if (!hasValidLookup && !hasRules) {
  //   errors.push(createError('Enable lookup or add rule'));
  // } else if (holdingsLookup?.enabled && !holdingsLookup.symbolColumn) {
  //   // Error: Holdings Lookup enabled but no column selected
  //   errors.push(createError('Select lookup column'));
  // }

  // Validate existing rules - skip empty rules (instrument identifiers are optional)
  rules.forEach(rule => {
    if (isRuleEmpty(rule)) {
      return; // Skip validation for empty rules
    }
    const ruleValidation = validateRule(rule);
    errors.push(...ruleValidation.errors);
    warnings.push(...ruleValidation.warnings);
  });

  return createValidationResult(errors, warnings);
};

/**
 * 5. MULTI_COLUMN_RULES_WITH_CATEGORIES
 * Examples: Buy/Sell, Deposit/Withdraw Type, FX Spot Type, Fee Type, Settlement Account, Deduction/Rebate
 * Some sections (BUY_SELL, DEPOSIT_WITHDRAW_TYPE, FX_SPOT_TYPE, DEDUCTION_REBATE) support sign detection
 */
export const validateMultiColumnRulesWithCategories = (
  config: SectionConfig
): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Sign detection validation (only if signDetection.sourceColumn is set)
  const signDetection = config.signDetection;
  if (signDetection?.sourceColumn) {
    const isSignDetectionComplete =
      signDetection.negativeCategory && signDetection.positiveCategory;

    // If sourceColumn is set, both category fields are required
    if (!signDetection.negativeCategory) {
      errors.push(createError('Select negative category for sign detection'));
    }
    if (!signDetection.positiveCategory) {
      errors.push(createError('Select positive category for sign detection'));
    }

    // Validate that negative and positive categories are different
    if (
      signDetection.negativeCategory &&
      signDetection.positiveCategory &&
      signDetection.negativeCategory === signDetection.positiveCategory
    ) {
      errors.push(
        createError('Negative and positive categories must be different')
      );
    }

    // If sign detection is fully configured, skip category rules validation
    // Sign detection is an alternative to explicit rules
    if (isSignDetectionComplete) {
      return createValidationResult(errors, warnings);
    }
  }

  // Standard category rules validation
  const categoryRules = config.categoryRules || [];

  // Error: At least one rule exists per category
  categoryRules.forEach(categorySection => {
    if (!categorySection.rules || categorySection.rules.length === 0) {
      errors.push(createError(`Add rule for ${categorySection.categoryName}`));
      return;
    }

    // Validate each rule within the category
    categorySection.rules.forEach(rule => {
      const ruleValidation = validateRule(rule);
      errors.push(...ruleValidation.errors);
      warnings.push(...ruleValidation.warnings);
    });
  });

  return createValidationResult(errors, warnings);
};

/**
 * 6. MATH_OPERATIONS
 * Examples: Commission, Other Charges
 */
export const validateMathOperations = (
  config: SectionConfig,
  sectionId: string
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Optional sections can be left empty
  const optionalSections = [
    SectionId.OTHER_CHARGES,
    SectionId.ACCRUED_INTEREST,
  ];
  if (optionalSections.includes(sectionId as SectionId)) {
    const mathOperation = config.mathOperation;
    const hasVariables =
      mathOperation?.variables &&
      Object.keys(mathOperation.variables).length > 0;
    const hasFormula =
      mathOperation?.formula && mathOperation.formula.trim().length > 0;

    // If completely empty, it's valid (optional)
    if (!hasVariables && !hasFormula) {
      return createValidationResult();
    }
  }

  const mathOperation = config.mathOperation;

  // Error: At least one variable must be assigned
  if (
    !mathOperation?.variables ||
    Object.keys(mathOperation.variables).length === 0
  ) {
    errors.push(createError('Assign at least 1 variable'));
  }

  // Error: Formula must be valid
  if (
    mathOperation?.variables &&
    Object.keys(mathOperation.variables).length > 0
  ) {
    const formulaValidation = validateFormulaSyntax(
      mathOperation.formula || '',
      mathOperation.variables
    );
    if (!formulaValidation.isValid && formulaValidation.error) {
      errors.push(createError('Fix formula syntax'));
    }
  }

  return createValidationResult(errors);
};

/**
 * 7. CURRENCY
 * Examples: Currency, Buy Currency, Sell Currency
 */
export const validateCurrency = (config: SectionConfig): ValidationResult => {
  const errors: ValidationError[] = [];

  // Error: Column must be selected
  const columnMappings = config.columnMappings || [];
  if (columnMappings.length === 0 || !columnMappings[0]?.sourceColumn) {
    errors.push(createError('Select column'));
  }

  // Error: At least one currency mapping with ≥1 variation
  const currencyMappings = config.currencyMappings || [];
  const validCurrencies = currencyMappings.filter(
    mapping => mapping.variations && mapping.variations.length > 0
  );

  if (validCurrencies.length === 0) {
    errors.push(createError('Add at least 1 currency'));
  }

  return createValidationResult(errors);
};
